/* import __COLOCATED_TEMPLATE__ from './unified-content.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { EntityType } from 'embercom/models/data/entity-types';

import KnowledgeHubApi from 'embercom/lib/knowledge-hub/list-api';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import type { SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { FolderFilterOption } from 'embercom/lib/content-service/search-api';
import {
  ALL_CONTENT_COLUMN_DATA,
  KNOWLEDGE_HUB_REQUIRED_COLUMNS,
  DEFAULT_SELECTABLE_COLUMNS,
  type TableColumn,
  type ColumnData,
  IMPORT_SOURCE_IN_PROGRESS_STATUSES,
  IMPORT_SOURCE_STATUSES,
} from 'embercom/lib/knowledge-hub/constants';
import { RUN_STATUSES } from 'embercom/lib/external-content/constants';
import { type ContentCardType } from './content-cards';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';
import {
  IMPORT_JOB_DELETE_STATUS_ID,
  IMPORT_JOB_STATUS_EVENT_ID,
  type UpdateImportSourceStatusEvent,
  type ImportServiceImportSourceDeletedEvent,
} from 'embercom/services/knowledge-hub-service';
import {
  type ContentImportRunEvent,
  EVENT_SERVICE_ID,
} from 'embercom/services/content-import-service';

import { getOwner } from '@ember/application';
import type Store from '@ember-data/store';

// Grouped argument interfaces
export interface HeaderArgs {
  showSearch?: boolean;
  showGeneralFilters?: boolean;
  showAudienceFilter?: boolean;
  title?: string;
  showHamburgerMenu?: boolean;
  showActionButtons?: boolean;
  showPreview?: boolean;
  onToggleSidebar?: () => void;
  togglePreview?: () => void;
  onNewContent?: () => void;
  customSearchPlaceholder?: string;
  customContentTypes?: string[];
}

export interface ContentSourceArgs {
  showTotalCount?: boolean;
  defaultColumns?: string[];
  openSectionId?: string;
  setOpenSectionId?: (sectionId: string) => void;
  showContentCards?: boolean;
  showContentSources?: boolean;
  onContentModal?: (contentType: ContentCardType) => void;
  showModal?: (syncSourceType: string | undefined, entityType: EntityType) => Promise<void>;

  // Translation controls
  showAllTranslations?: boolean;
  toggleTranslations?: () => void;

  // Sorting controls
  sortBy?: string;
  setSortBy?: (sortBy?: string) => void;
  sortDirection?: SortDirection;
  setSortDirection?: (sortDirection?: string) => void;
}

interface Signature {
  Args: {
    showHeader?: boolean;
    finIsLive?: boolean;
    headerArgs?: HeaderArgs;
    contentSourceArgs?: ContentSourceArgs;
    nativeContent?: boolean;
    // Routes for folder navigation. Passed down from route templates
    // to keep components decoupled from router. Defaults to Knowledge Hub if not provided.
    parentFolderRoute?: string;
    allContentRoute?: string;
  };
}
export default class UnifiedContent extends Component<Signature> {
  @service declare appService: AppService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare unifiedContentState: UnifiedContentStateService;
  @service declare realTimeEventService: { on: Function; off: Function; subscribeTopics: Function };
  @service declare store: Store;

  @tracked previewLocale = 'en';
  @tracked previewMode: 'agent' | 'copilot' = 'agent';
  @tracked columns: TableColumn[] = [];
  private localListApi?: KnowledgeHubApi;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this.columns = this.defaultColumns;
    this.previewLocale = this.appService.app.locale || 'en';
    if (this.appService.app.canUseFinPreviewAutoLanguageDetection) {
      this.previewLocale = 'auto';
    }
    this.unifiedContentState.resetFilters();
    this.initializeListApi();
    this.subscribeToImportEvents();
  }

  // Returns the current API instance.
  // Returns the API from knowledgeHubService if available

  get listApi(): KnowledgeHubApi | undefined {
    return this.knowledgeHubService.api || this.localListApi;
  }

  set listApi(value: KnowledgeHubApi | undefined) {
    this.localListApi = value;
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeFromImportEvents();
  }

  private async initializeListApi() {
    // Use the route's API if available, otherwise create our own
    if (this.knowledgeHubService.api) {
      this.listApi = this.knowledgeHubService.api;
      this.unifiedContentState.registerListApi(this.listApi);
    } else {
      // Ensure folders are loaded into Ember Data store so content wrappers can resolve their parent folder relationships for proper tree structure rendering.
      await this.knowledgeHubService.fetchFoldersOnce();

      let defaultSearchParams = {
        app_id: this.appService.app.id as string,
        content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
        folder_filter_option: FolderFilterOption.None,
        object_types: this.knowledgeHubService.getSearchObjectTypes(),
        sort_by: 'title',
        sort_direction: 'asc' as SortDirection,
        native_content: this.args.nativeContent ?? false,
      };

      this.listApi = new KnowledgeHubApi(getOwner(this), defaultSearchParams);
      this.unifiedContentState.registerListApi(this.listApi);
      await this.listApi.loadPage();
    }
  }

  setPreviewLocale(locale: string) {
    this.previewLocale = locale;
  }

  setPreviewMode(mode: 'agent' | 'copilot') {
    this.previewMode = mode;
  }

  noop() {
    // No-op function for optional callbacks
  }

  resetFilters = () => {
    this.unifiedContentState.resetFilters();
    this.listApi?.search();
  };

  get defaultColumns() {
    let spacerColumn: TableColumn = { valuePath: 'spacer' };
    // Fin/Content shows only Status & AI Agent Columns
    // KH shows Status, Help Center, Copilot, AI Agent Columns.
    // Based on route, if null uses defualt of KH
    let columns = [
      ...KNOWLEDGE_HUB_REQUIRED_COLUMNS,
      ALL_CONTENT_COLUMN_DATA.contentStatus,
      ...(!this.args.parentFolderRoute
        ? [ALL_CONTENT_COLUMN_DATA.helpCenterState, ALL_CONTENT_COLUMN_DATA.copilotState]
        : []),
      ALL_CONTENT_COLUMN_DATA.chatbotState,
      spacerColumn,
    ];
    return columns;
  }

  get showEmptyState(): boolean {
    return !this.listApi?.isLoadingPage && this.listApi?.contentWrappers.length === 0;
  }

  get hasAppliedFilter(): boolean {
    return this.unifiedContentState.hasAppliedFilter;
  }

  get hasActiveSearch(): boolean {
    return this.unifiedContentState.hasActiveSearch;
  }

  get selectableColumns(): ColumnData[] {
    return DEFAULT_SELECTABLE_COLUMNS;
  }

  get showContentCards(): boolean {
    return !!this.args?.contentSourceArgs?.showContentCards && !this.hasActiveSearch;
  }

  get shouldShowArticlesFolder(): boolean {
    return !this.args.nativeContent && !this.hasActiveSearch;
  }

  updatedSelectedTableColumns(columns: string[]) {
    let selectedColumns = columns.map((col) => ALL_CONTENT_COLUMN_DATA[col]);
    this.columns = KNOWLEDGE_HUB_REQUIRED_COLUMNS.concat(selectedColumns);
  }
  private subscribeToImportEvents() {
    this.realTimeEventService.on(IMPORT_JOB_STATUS_EVENT_ID, this, 'handleImportStatusChange');
    this.realTimeEventService.on(IMPORT_JOB_DELETE_STATUS_ID, this, 'handleImportSourceDeleted');
    this.realTimeEventService.on(EVENT_SERVICE_ID, this, 'handleContentImportRunStatus');
  }
  private unsubscribeFromImportEvents() {
    this.realTimeEventService.off(IMPORT_JOB_STATUS_EVENT_ID, this, 'handleImportStatusChange');
    this.realTimeEventService.off(IMPORT_JOB_DELETE_STATUS_ID, this, 'handleImportSourceDeleted');
    this.realTimeEventService.off(EVENT_SERVICE_ID, this, 'handleContentImportRunStatus');
  }

  // event for sources like zendesk, salesorce, notion, confluence, etc.
  async handleImportStatusChange(event: UpdateImportSourceStatusEvent) {
    let source = this.store.peekRecord('import-service/import-source', event.import_source_id);
    if (!source) {
      return;
    }

    let previousStatus = source.status;
    source.status = event.status;

    if (this.shouldRefreshOnStatusChange(event.status, previousStatus)) {
      await this.reloadImportData();
    }
  }

  async handleImportSourceDeleted(event: ImportServiceImportSourceDeletedEvent) {
    if (event.is_deleted) {
      await this.reloadImportData();
    }
  }

  // event for website import.
  async handleContentImportRunStatus(event: ContentImportRunEvent) {
    let isCompleted =
      event?.event_type === RUN_STATUSES.completed || event?.event_type === RUN_STATUSES.failed;
    if (isCompleted) {
      await this.reloadImportData();
    }
  }

  private shouldRefreshOnStatusChange(currentStatus: string, previousStatus: string): boolean {
    let isStarting =
      IMPORT_SOURCE_IN_PROGRESS_STATUSES.includes(currentStatus) &&
      !IMPORT_SOURCE_IN_PROGRESS_STATUSES.includes(previousStatus);
    let isComplete = currentStatus === IMPORT_SOURCE_STATUSES.success;

    return isStarting || isComplete;
  }

  private async reloadImportData(): Promise<void> {
    await this.store.findAll('import-service/import-source', { reload: true });
    await this.knowledgeHubService.fetchFoldersOnce(true);
    await this.listApi?.search();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::UnifiedContent::UnifiedContent': typeof UnifiedContent;
    'knowledge-hub/unified-content/unified-content': typeof UnifiedContent;
  }
}
